import {
  type Participant,
  type ParticipantUser,
  renderParticipantName,
  type User,
} from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  createColumnHelper,
  DataTable,
  ScrollArea,
  TransferList,
  type TransferListComponents,
  type TransferListContentItem,
  type TransferListOption,
  useDebouncedCallback,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { createModal } from '../../components/generic/Modal';
import { UserDescription } from '../../components/user/UserDescription';
import { useCreateParticipantUsersMutation } from '../../graphql/hooks/useCreateParticipantUsersMutation';
import { useDeleteParticipantUsersMutation } from '../../graphql/hooks/useDeleteParticipantUsersMutation';
import { useParticipantUsersQuery } from '../../graphql/hooks/useParticipantQuery';
import { useUsersQuery } from '../../graphql/hooks/useUsersQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ManageParticipantUserLabelsControl } from '../labels/ManageParticipantUserLabelsControl';

interface Props {
  participant: Participant;
}

export const ParticipantTeamMemberManageModal = createModal<Props>((props) => {
  const { participant, modal } = props;
  const { organization, successToast, errorToast } = useAppContext();
  const { hide, remove, visible } = modal;

  const [nameFilter, setNameFilter] = React.useState('');
  const onChangeName = useDebouncedCallback(setNameFilter, 1000);

  const createParticipantUsersMutation = useCreateParticipantUsersMutation();
  const deleteParticipantUsersMutation = useDeleteParticipantUsersMutation();

  const usersQuery = useUsersQuery({
    organizationId: organization.id,
    request: {
      name: nameFilter,
    },
  });

  const participantUsersQuery = useParticipantUsersQuery({
    organizationId: organization.id,
    participantId: participant.id,
  });

  const value: TransferListOption<ParticipantUser>[] = React.useMemo(() => {
    const assignedUsers =
      (participantUsersQuery.data?.organization?.participant?.assignedUsers as ParticipantUser[]) ??
      [];

    return assignedUsers.map((assignedUser) => ({
      label: assignedUser.user.name,
      value: assignedUser.user.id,
      raw: assignedUser,
    }));
  }, [participantUsersQuery.data?.organization?.participant?.assignedUsers]);

  const options: TransferListOption<ParticipantUser>[] = React.useMemo(() => {
    const users = (usersQuery.data?.organization?.users.results as User[]) ?? [];

    return users.map((user) => ({
      label: user.name,
      value: user.id,
      raw: {
        id: user.id,
        labels: [],
        user,
      },
    }));
  }, [usersQuery.data?.organization?.users.results]);

  const leftColumns = React.useMemo(() => {
    const columnHelper = createColumnHelper<TransferListContentItem<ParticipantUser>>();

    return [
      columnHelper.display({
        id: 'select',
        size: 32,
        minSize: 32,
        maxSize: 32,
        header: (ps) => {
          const rows = ps.table.getCoreRowModel().rows;
          const isAllChecked = !P.isEmpty(rows) && rows.every((row) => row.original.isSelected);
          const isIndeterminate = rows.some((row) => row.original.isSelected) && !isAllChecked;
          const onChange = () => {
            rows.forEach((row) => {
              if (!row.original.isSelected) {
                row.original.onClick();
              }

              if (isAllChecked) {
                row.original.onClick();
              }
            });
          };

          return (
            <ChakraV3.Checkbox
              checked={isIndeterminate ? 'indeterminate' : isAllChecked}
              onChange={onChange}
              size='sm'
              colorPalette='brand'
            />
          );
        },
        cell: (ps) => {
          return (
            <ChakraV3.Checkbox
              disabled={false}
              checked={ps.row.original.isSelected}
              onChange={ps.row.original.onClick}
              size='sm'
              colorPalette='brand'
            />
          );
        },
      }),
      columnHelper.display({
        header: 'Name',
        minSize: 380,
        maxSize: 380,
        cell: (props) => (
          <UserDescription
            fontSize='sm'
            name={props.row.original.raw.user.name}
            secondary={props.row.original.raw.user.title}
            picture={props.row.original.raw.user.picture}
          />
        ),
      }),
    ];
  }, []);

  const rightColumns = React.useMemo(() => {
    const columnHelper = createColumnHelper<TransferListContentItem<ParticipantUser>>();

    return [
      ...leftColumns,
      columnHelper.display({
        header: 'Labels',
        cell: (props) => (
          <ManageParticipantUserLabelsControl
            participantId={participant.id}
            participantUserId={props.row.original.raw.id}
            value={props.row.original.raw.labels.map((label) => label.id)}
          />
        ),
      }),
    ];
  }, [leftColumns, participant.id]);

  const components: TransferListComponents<ParticipantUser> = React.useMemo(() => {
    return {
      left: {
        Header: (props) => {
          return (
            <ChakraV3.Stack>
              <ChakraV3.HStack>
                <ChakraV3.Heading size='xl' fontWeight='bold'>
                  All users
                </ChakraV3.Heading>
                <ChakraV3.Spacer />
                <ChakraV3.HStack>
                  <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
                    Items
                  </ChakraV3.Text>
                  <ChakraV3.Text fontSize='sm' color='secondary'>
                    {props.selectedItemCount} / {props.itemCount} selected
                  </ChakraV3.Text>
                </ChakraV3.HStack>
              </ChakraV3.HStack>
              <ChakraV3.Input
                defaultValue={nameFilter}
                onChange={(e) => onChangeName(e.target.value)}
                size='sm'
                w='3xs'
                placeholder='All users'
              />
            </ChakraV3.Stack>
          );
        },
        Content: (props) => {
          return (
            <ChakraV3.Stack h='full' w='lg' overflowY='auto'>
              <ScrollArea>
                <DataTable
                  columns={leftColumns}
                  data={props.options}
                  size='sm'
                  renderEmpty={() => <ChakraV3.Text py={8}>No users found</ChakraV3.Text>}
                  isLoading={usersQuery.isLoading}
                />
              </ScrollArea>
            </ChakraV3.Stack>
          );
        },
      },
      right: {
        Header: (props) => {
          return (
            <ChakraV3.Stack pb={10}>
              <ChakraV3.HStack>
                <ChakraV3.Heading size='xl' fontWeight='bold'>
                  Assigned users
                </ChakraV3.Heading>
                <ChakraV3.Spacer />
                <ChakraV3.HStack>
                  <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
                    Items
                  </ChakraV3.Text>
                  <ChakraV3.Text fontSize='sm' color='secondary'>
                    {props.selectedItemCount} / {props.itemCount} selected
                  </ChakraV3.Text>
                </ChakraV3.HStack>
              </ChakraV3.HStack>
            </ChakraV3.Stack>
          );
        },
        Content: (props) => {
          return (
            <ChakraV3.Stack h='full' w='full' overflowY='auto'>
              <ScrollArea>
                <DataTable
                  columns={rightColumns}
                  data={props.options}
                  size='sm'
                  renderEmpty={() => <ChakraV3.Text py={8}>No users assigned</ChakraV3.Text>}
                  isLoading={participantUsersQuery.isLoading}
                />
              </ScrollArea>
            </ChakraV3.Stack>
          );
        },
      },
    };
  }, [
    leftColumns,
    rightColumns,
    nameFilter,
    participantUsersQuery.isLoading,
    usersQuery.isLoading,
    onChangeName,
  ]);

  const onChange = React.useCallback(
    async (values: TransferListOption<ParticipantUser>[]) => {
      const existingUserIds = value.filter((value) => value.raw.user.id);
      const usersToAdd = values.filter((value) => !existingUserIds.includes(value));
      const usersToRemove = existingUserIds.filter((value) => !values.includes(value));

      if (!P.isEmpty(usersToAdd)) {
        await createParticipantUsersMutation
          .mutateAsync({
            organizationId: organization.id,
            requests: usersToAdd.map((user) => ({
              participantId: participant.id,
              userId: user.raw.user.id,
              labelIds: [],
            })),
          })
          .then(() => {
            successToast('Users added successfully');
          })
          .catch((err) => {
            errorToast(`Error adding users: ${err.message}`);
          });
      }

      if (!P.isEmpty(usersToRemove)) {
        await deleteParticipantUsersMutation
          .mutateAsync({
            organizationId: organization.id,
            participantId: participant.id,
            userIds: usersToRemove.map((user) => user.raw.user.id),
          })
          .then(() => {
            successToast('Users removed successfully');
          })
          .catch((err) => {
            errorToast(`Error removing users: ${err.message}`);
          });
      }

      return;
    },
    [
      value,
      createParticipantUsersMutation,
      deleteParticipantUsersMutation,
      organization.id,
      participant.id,
      successToast,
      errorToast,
    ],
  );

  const content = P.run(() => {
    return (
      <TransferList
        options={options}
        value={value}
        components={components}
        onChange={onChange}
        isLeftLoading={deleteParticipantUsersMutation.isLoading}
        isRightLoading={createParticipantUsersMutation.isLoading}
      />
    );
  });

  return (
    <ChakraV3.Dialog.Root
      open={visible}
      onOpenChange={hide}
      onExitComplete={remove}
      size='cover'
      scrollBehavior='inside'
    >
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <ChakraV3.Dialog.Content>
          <ChakraV3.DialogTitleSubtitle
            title={`Manage ${renderParticipantName(participant)}'s team members`}
            subtitle='You can add or remove team members from this participant.'
          />
          <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
          <ChakraV3.Dialog.Footer>
            <ChakraV3.HStack>
              <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                Close
              </ChakraV3.Button>
            </ChakraV3.HStack>
          </ChakraV3.Dialog.Footer>
        </ChakraV3.Dialog.Content>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
